import { CbhIcon, Text, TextButton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
// eslint-disable-next-line no-restricted-imports
import { Accordion, AccordionDetails, AccordionSummary, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { PlacementSortOption } from "@src/appV2/redesign/Placements/types/fetchPlacements.schema";
import { Fragment, useMemo } from "react";

import { ButtonInternalLink } from "../../../components/ButtonInternalLink";
import { LoadingButton } from "../../../components/LoadingButton";
import {
  transformResponseIntoPlacement,
  useFetchPaginatedPlacements,
} from "../../api/useFetchPaginatedPlacements";
import { applicationActiveSectionStorageKey, showApplicationsStorageKey } from "../../utils";
import { placementWithDetailsToData } from "../../utils/placement-data";
import { AppliedPlacementCard } from "../AppliedPlacementCard/AppliedPlacementCard";
import { useGetPlacementFilters } from "../PlacementDetailsPage/hooks/useGetPlacementFilters";
import { PlacementsLoader } from "../PlacementsLoader";
import { type PlacementsAccordionSectionProps } from "./types";

export function PlacementsAccordionSectionV2(props: PlacementsAccordionSectionProps) {
  const {
    title,
    hasInterviews,
    placementCandidate,
    worker,
    filter,
    showApplications,
    initialExpanded,
  } = props;

  const [expanded, setExpanded] = useLocalStorage(
    applicationActiveSectionStorageKey(title),
    initialExpanded ?? false
  );

  const [, setShowApplications] = useLocalStorage<boolean>(
    showApplicationsStorageKey,
    showApplications
  );

  const placementFilters = useGetPlacementFilters({
    filter,
    showApplications,
    worker,
    hasInterviews,
    enabled: isDefined(placementCandidate),
  });

  const {
    data: placementsData,
    isLoading: isPlacementsLoading,
    isSuccess: isPlacementsSuccess,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useFetchPaginatedPlacements(
    {
      workerId: worker.userId,
      placementCandidateId: placementCandidate.id,
      filter: placementFilters,
      shouldLogEvent: true,
      sort: hasInterviews
        ? PlacementSortOption.INTERVIEW_START
        : PlacementSortOption.APPLICATIONS_APPLIED_DATE,
    },
    {
      enabled: isDefined(worker.userId) && showApplications,
    }
  );

  // total count across all fetched pages – used in the accordion header
  const totalPlacementsCount = useMemo(() => {
    if (!placementsData) {
      return 0;
    }

    return placementsData.pages.reduce((sum, page) => sum + page.data.length, 0);
  }, [placementsData]);

  return (
    <Accordion
      disableGutters
      expanded={expanded || (!isPlacementsLoading && totalPlacementsCount === 0)}
      sx={{
        backgroundColor: (theme) => theme.palette.background.tertiary,
        "&:before": {
          display: "none",
        },
      }}
      onChange={(_, newExpanded) => {
        setExpanded(newExpanded);
      }}
    >
      <AccordionSummary
        expandIcon={<CbhIcon type="chevron-down" />}
        sx={{ minHeight: "40px", "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Stack direction="row" alignItems="end" spacing={3}>
          <Text variant="h5">{title}</Text>
          <Text variant="body2" sx={{ color: (theme) => theme.palette.text.tertiary }}>
            {totalPlacementsCount}
          </Text>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 4 }}>
        <Stack>
          {isPlacementsLoading && <PlacementsLoader count={1} />}

          {isPlacementsSuccess && (placementsData?.pages[0]?.data.length ?? 0) === 0 && (
            <Stack justifyContent="center" flex={1} alignItems="center" sx={{ pt: 5 }} spacing={2}>
              <Text variant="body1" sx={{ textAlign: "center" }}>
                You have not applied to any jobs yet!{" "}
              </Text>
              <Text>
                Please visit the{" "}
                <TextButton
                  LinkComponent={ButtonInternalLink}
                  onClick={() => {
                    setShowApplications(false);
                  }}
                >
                  Open Staff Jobs tab
                </TextButton>{" "}
                to apply to jobs.
              </Text>
            </Stack>
          )}

          {isDefined(placementsData) && placementsData.pages.length > 0 && (
            <Stack direction="column" spacing={6}>
              {placementsData?.pages.flatMap((placementPage, pageIndex) => {
                return placementPage.data.map((placement, placementIndex) => {
                  const placementWithDetails = transformResponseIntoPlacement(
                    placement,
                    placementPage.included
                  );

                  return (
                    <Fragment key={placementWithDetails.id}>
                      <AppliedPlacementCard
                        key={placementWithDetails.id}
                        placement={placementWithDetailsToData(placementWithDetails)}
                        placementCandidate={placementCandidate}
                        pageIndex={pageIndex}
                        placementIndex={placementIndex}
                        worker={worker}
                        source={hasInterviews ? "booked_interviews" : "sent_applications"}
                      />
                    </Fragment>
                  );
                });
              })}
            </Stack>
          )}

          {hasNextPage && (
            <Stack direction="row" justifyContent="center">
              <LoadingButton
                sx={{ my: 5, mb: 10 }}
                variant="text"
                isLoading={isFetchingNextPage}
                onClick={async () => {
                  await fetchNextPage();
                  logEvent(APP_V2_APP_EVENTS.PLACEMENTS_PAGE_ACTIONS, {
                    placementCandidateId: placementCandidate.id,
                    filters: { ...filter, distanceInMiles: filter?.distanceInMiles?.$lt },
                    action: "load_more_clicked",
                  });
                }}
              >
                Load more
              </LoadingButton>
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
